// @givmo/org-console — the approver's two dialogs (shell contract v3, T5.5): the tier dialog an
// APPROVE opens, pre-set to the requested role, and the note dialog a DENY opens.
//
// Both are PRESENTATIONAL — the requests panel owns the two writes and their refusals, exactly as the
// invites panel owns the revoke behind its confirm dialog. What each renders is a decision with
// nothing else on screen to act on: one tier group, or one optional note, and two buttons.

import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Textarea,
  testIdProp,
} from '@givmo/design-system';
import {
  ROLE_MEMBER,
  ROLE_OWNER,
  moduleMemberDescription,
  moduleOwnerDescription,
  moduleTier,
  moduleTierLabel,
  type OrgTier,
} from './org-tiers';

export interface ApproveAccessRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requesterName: string;
  appLabel: string;
  /** The tier the requester asked for — the dialog's initial selection. */
  requestedRole: string;
  onApprove: (grantedRole: OrgTier) => void;
  pending?: boolean;
  /** A refusal sentence to show, when the write failed. */
  error?: string | null;
  testIdPrefix: string;
}

/** Approve: the tier dialog, PRE-SET to the requested role; the approver may change it (R6). */
export function ApproveAccessRequestDialog({
  open,
  onOpenChange,
  requesterName,
  appLabel,
  requestedRole,
  onApprove,
  pending = false,
  error,
  testIdPrefix,
}: ApproveAccessRequestDialogProps) {
  const prefix = `${testIdPrefix}-approve`;
  const [role, setRole] = useState<string>(moduleTier(requestedRole));
  useEffect(() => {
    if (open) setRole(moduleTier(requestedRole));
  }, [open, requestedRole]);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <Dialog.Content
        aria-label={`Approve ${requesterName}`}
        className="flex flex-col gap-16"
        testId={`${prefix}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Approve {requesterName}
        </Dialog.Title>
        <p className="font-sans text-s14 text-text-tertiary">
          Approving gives {requesterName} {appLabel}. They asked for{' '}
          {moduleTierLabel(requestedRole)}; you can change it.
        </p>
        <RadioTileGroup
          value={role}
          onValueChange={setRole}
          aria-label="Choose access level"
          className="gap-12"
          disabled={pending}
          testId={`${prefix}-tier`}
        >
          <RadioTile
            value={ROLE_MEMBER}
            label={moduleTierLabel(ROLE_MEMBER)}
            description={moduleMemberDescription(appLabel)}
            testId={`${prefix}-tier-member`}
          />
          <RadioTile
            value={ROLE_OWNER}
            label={moduleTierLabel(ROLE_OWNER)}
            description={moduleOwnerDescription(appLabel)}
            testId={`${prefix}-tier-owner`}
          />
        </RadioTileGroup>
        {error != null && error.length > 0 && (
          <ErrorMessage testId={`${prefix}-error`}>{error}</ErrorMessage>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={pending}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${prefix}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={pending}
            onClick={() => onApprove(moduleTier(role))}
            testId={`${prefix}-submit`}
          >
            Approve
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export interface DenyAccessRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requesterName: string;
  appLabel: string;
  onDeny: (note: string) => void;
  pending?: boolean;
  error?: string | null;
  testIdPrefix: string;
}

/** Deny: "Tell the requester why? (optional)" + Deny. The note reaches them verbatim. */
export function DenyAccessRequestDialog({
  open,
  onOpenChange,
  requesterName,
  appLabel,
  onDeny,
  pending = false,
  error,
  testIdPrefix,
}: DenyAccessRequestDialogProps) {
  const prefix = `${testIdPrefix}-deny`;
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <Dialog.Content
        aria-label={`Deny ${requesterName}`}
        className="flex flex-col gap-16"
        testId={`${prefix}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Deny {requesterName}
        </Dialog.Title>
        <p className="font-sans text-s14 text-text-tertiary">
          {requesterName} will be told their request to open {appLabel} was declined. They can ask
          again.
        </p>
        <Textarea
          label="Tell the requester why? (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="One sentence they will see on their tile and in their email."
          rows={3}
          maxLength={1000}
          disabled={pending}
          testId={`${prefix}-note`}
        />
        {error != null && error.length > 0 && (
          <ErrorMessage testId={`${prefix}-error`}>{error}</ErrorMessage>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={pending}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${prefix}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="danger"
            size="md"
            loading={pending}
            onClick={() => onDeny(note.trim())}
            testId={`${prefix}-submit`}
          >
            Deny
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
